/**
 * Phase 20 Plan 20-02 — TanStack Query v5 hooks for notifications + alerts.
 *
 * Query key shapes:
 *   alerts list:         ['alerts', 'list', filters]
 *   alert detail:        ['alerts', 'detail', id]
 *   unread count:        ['alerts', 'unread-count']
 *   recipients list:     ['notifications', 'recipients', { includeInactive, page, pageSize }]
 *   recipient detail:    ['notifications', 'recipients', id]
 *   subscriptions:       ['notifications', 'subscriptions', recipientId]
 *   notification types:  ['notifications', 'types']
 *
 * Mirrors useKpiQueries.ts patterns (Phase 17).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  acknowledgeAlertRequest,
  addPhoneRequest,
  createRecipientRequest,
  deactivateRecipientRequest,
  deletePhoneRequest,
  dismissAlertRequest,
  getAlertDetailRequest,
  getAlertsRequest,
  getAlertsUnreadCountRequest,
  getNotificationTypesRequest,
  getRecipientDetailRequest,
  getRecipientsRequest,
  reactivateRecipientRequest,
  updatePhoneRequest,
  updateRecipientRequest,
  updateSubscriptionsRequest,
} from '@/services/notifications';
import type {
  AddPhoneRequest,
  AlertDto,
  AlertListQuery,
  CreateRecipientRequest,
  NotificationTypeInfoDto,
  PhoneNumberDto,
  PaginatedResult,
  RecipientDetailDto,
  RecipientListItemDto,
  SubscriptionDto,
  UnreadCountDto,
  UpdatePhoneRequest,
  UpdateRecipientRequest,
  UpdateSubscriptionsRequest,
} from '@/types/notifications';

// ---------------------------------------------------------------------------
// Alert query hooks
// ---------------------------------------------------------------------------

/**
 * Paginated alerts list with optional filters. staleTime 10s.
 *
 * REVIEW FF-005: callers that gate by `notifications.view` permission MUST
 * pass `{ enabled: hasView }` so the query doesn't fire for unauthorised
 * users. Defaults to enabled for backwards-compatibility.
 */
export function useAlertsQuery(
  filters: AlertListQuery = {},
  options: { readonly enabled?: boolean } = {},
) {
  return useQuery<PaginatedResult<AlertDto>, ApiError>({
    queryKey: ['alerts', 'list', filters],
    queryFn: () => getAlertsRequest(filters),
    enabled: options.enabled !== false,
    staleTime: 10_000,
  });
}

/** Full detail for a single alert. */
export function useAlertDetailQuery(id: number, enabled = true) {
  return useQuery<AlertDto, ApiError>({
    queryKey: ['alerts', 'detail', id],
    queryFn: () => getAlertDetailRequest(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });
}

/**
 * Unread alert count — polls every 30 seconds per D-186.
 * Pass `enabled: false` when the user lacks `notifications.view` permission.
 *
 * REVIEW FP-003: `refetchIntervalInBackground: false` so the timer pauses
 * when the tab is hidden — prevents ~120 useless requests/hour/tab and
 * conserves battery on mobile.
 */
export function useUnreadCountQuery(options: { readonly enabled?: boolean } = {}) {
  return useQuery<UnreadCountDto, ApiError>({
    queryKey: ['alerts', 'unread-count'],
    queryFn: getAlertsUnreadCountRequest,
    enabled: options.enabled !== false,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });
}

// ---------------------------------------------------------------------------
// Alert mutation hooks
// ---------------------------------------------------------------------------

/** Acknowledge a single alert. Invalidates all ['alerts'] on success. */
export function useAcknowledgeMutation() {
  const qc = useQueryClient();
  return useMutation<AlertDto, ApiError, number>({
    mutationFn: acknowledgeAlertRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/** Dismiss a single alert. Invalidates all ['alerts'] on success. */
export function useDismissMutation() {
  const qc = useQueryClient();
  return useMutation<AlertDto, ApiError, number>({
    mutationFn: dismissAlertRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Notification settings query hooks
// ---------------------------------------------------------------------------

interface RecipientsQueryParams {
  readonly includeInactive?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
}

/** Paginated recipients list. */
export function useRecipientsQuery(params: RecipientsQueryParams = {}) {
  return useQuery<PaginatedResult<RecipientListItemDto>, ApiError>({
    queryKey: ['notifications', 'recipients', params],
    queryFn: () => getRecipientsRequest(params),
    staleTime: 10_000,
  });
}

/** Full recipient detail (includes phones + subscriptions). */
export function useRecipientDetailQuery(id: number, enabled = true) {
  return useQuery<RecipientDetailDto, ApiError>({
    queryKey: ['notifications', 'recipients', id],
    queryFn: () => getRecipientDetailRequest(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
    staleTime: 10_000,
  });
}

/** Notification type metadata (constant — staleTime: Infinity). */
export function useNotificationTypesQuery() {
  return useQuery<NotificationTypeInfoDto[], ApiError>({
    queryKey: ['notifications', 'types'],
    queryFn: getNotificationTypesRequest,
    staleTime: Infinity,
  });
}

// ---------------------------------------------------------------------------
// Notification settings mutation hooks
// ---------------------------------------------------------------------------

/** Create a new notification recipient. Invalidates recipients list on success. */
export function useCreateRecipientMutation() {
  const qc = useQueryClient();
  return useMutation<RecipientDetailDto, ApiError, CreateRecipientRequest>({
    mutationFn: createRecipientRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients'] });
    },
  });
}

interface UpdateRecipientVars {
  readonly id: number;
  readonly body: UpdateRecipientRequest;
}

/** Update recipient display name / email / isActive. */
export function useUpdateRecipientMutation() {
  const qc = useQueryClient();
  return useMutation<RecipientDetailDto, ApiError, UpdateRecipientVars>({
    mutationFn: ({ id, body }) => updateRecipientRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients'] });
    },
  });
}

/** Deactivate (soft-delete) a recipient. */
export function useDeactivateRecipientMutation() {
  const qc = useQueryClient();
  return useMutation<RecipientDetailDto, ApiError, number>({
    mutationFn: deactivateRecipientRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients'] });
    },
  });
}

/** Reactivate a deactivated recipient. */
export function useReactivateRecipientMutation() {
  const qc = useQueryClient();
  return useMutation<RecipientDetailDto, ApiError, number>({
    mutationFn: reactivateRecipientRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients'] });
    },
  });
}

interface AddPhoneVars {
  readonly recipientId: number;
  readonly body: AddPhoneRequest;
}

/** Add a phone number to a recipient. */
export function useAddPhoneMutation() {
  const qc = useQueryClient();
  return useMutation<PhoneNumberDto, ApiError, AddPhoneVars>({
    mutationFn: ({ recipientId, body }) => addPhoneRequest(recipientId, body),
    onSuccess: (_data, { recipientId }) => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients', recipientId] });
    },
  });
}

interface UpdatePhoneVars {
  readonly recipientId: number;
  readonly phoneId: number;
  readonly body: UpdatePhoneRequest;
}

/** Update a phone's primary flag. */
export function useUpdatePhoneMutation() {
  const qc = useQueryClient();
  return useMutation<PhoneNumberDto, ApiError, UpdatePhoneVars>({
    mutationFn: ({ recipientId, phoneId, body }) => updatePhoneRequest(recipientId, phoneId, body),
    onSuccess: (_data, { recipientId }) => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients', recipientId] });
    },
  });
}

interface DeletePhoneVars {
  readonly recipientId: number;
  readonly phoneId: number;
}

/** Soft-delete a phone number. */
export function useDeletePhoneMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, DeletePhoneVars>({
    mutationFn: async ({ recipientId, phoneId }) => {
      await deletePhoneRequest(recipientId, phoneId);
      return undefined;
    },
    onSuccess: (_data, { recipientId }) => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients', recipientId] });
    },
  });
}

interface UpdateSubscriptionsVars {
  readonly recipientId: number;
  readonly body: UpdateSubscriptionsRequest;
}

/** Bulk update subscription matrix for a recipient. */
export function useUpdateSubscriptionsMutation() {
  const qc = useQueryClient();
  return useMutation<SubscriptionDto[], ApiError, UpdateSubscriptionsVars>({
    mutationFn: ({ recipientId, body }) => updateSubscriptionsRequest(recipientId, body),
    onSuccess: (_data, { recipientId }) => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'recipients', recipientId] });
    },
  });
}
